import { useCreateStationeryDraftMutation } from './ProductDetailsPage.graphql.generated';
import { StationeryDraftFormat } from '@graphql/generated-base';
import { useCardsRouterContext } from '@apps/card/Card.routes';

import { useExistingDraftsWarningContext } from '../ExistingDraftsWarningProvider';
import { useRestrictedInternationalShippingWarningContext } from '../RestrictedInternationalShippingWarningProvider';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { cloneDeep } from 'lodash-es';
import { CardEnvelopeRecipientAddressLayout } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';

import { resetCardTemplateDefaults } from '../DesignsGallery/utils';
import type { Format, MinimalRequiredTemplateDataForDraft } from '../DesignsGallery/DesignsGallery.types';
import type { StationeryProduct } from '@shared/utils/print-price/types';

type UseCustomizePaperCardArgs = {
  eventId?: string;
  currentProduct: StationeryProduct | undefined;
  supportedFormats: Format;
};

export const useCustomizePaperCard = (args: UseCustomizePaperCardArgs) => {
  const { eventId, supportedFormats, currentProduct } = args;
  const { existingDraftsWarningDialog, restrictedInternationalShippingWarningDialog, cardDraftCreated, pdpCustomizeNowClicked } = useCardTelemetry({});

  const { goToCustomizeDraft } = useCardsRouterContext();
  const { checkForExistingDraftOrCreateNew } = useExistingDraftsWarningContext();
  const { checkIfUserIsRestricted } = useRestrictedInternationalShippingWarningContext();

  const [createStationeryDraft, { loading }] = useCreateStationeryDraftMutation({
    refetchQueries: () => ['GetDigitalAndPaperDraftsByEventId', 'GetExistingEventStationeryDraftsWithTemplate'],
  });

  // TODO: https://withjoy.atlassian.net/browse/PRNT-1214
  const handleOnCustomizeCardClick = useEventCallback((template: MinimalRequiredTemplateDataForDraft, subtotalInMinorUnits?: number) => {
    checkIfUserIsRestricted(
      StationeryDraftFormat.print,
      { themeId: template.themeId, category: template.category },
      () =>
        // This callback is called when the RestrictedInternationalShipping warning dialog is about to be shown.
        restrictedInternationalShippingWarningDialog.enter(),
      () =>
        checkForExistingDraftOrCreateNew(
          template.id,
          async existingDraftsCount => {
            try {
              pdpCustomizeNowClicked({ themeId: template.themeId, supportedFormats, format: 'paper' });
              const clonedTemplate = cloneDeep(template);
              // We don't want to reset the paper type that user selected in the PDP page
              resetCardTemplateDefaults(clonedTemplate, template.category, { resetPaperType: false });
              const cardJSON = clonedTemplate.themeJSON;

              // Set the recipient address envelope defaults (this need to be made on the BE)
              cardJSON.card.envelope.recipientAddressLayout = CardEnvelopeRecipientAddressLayout.none;
              cardJSON.card.envelope.recipientAddressLayers = [];
              // Leave `recipientIdList` undefined: the Recipients step treats `undefined` as
              // "user has not yet touched the selection" and applies the default selection
              // (heads of household + singletons) on first visit.
              cardJSON.card.envelope.recipientIdList = undefined;

              if (!currentProduct) {
                throw new Error('Tried creating draft with no selected product.');
              }

              const response = await createStationeryDraft({
                variables: {
                  payload: {
                    eventId,
                    cardJSON,
                    stationeryTemplateId: template.id,
                    priceConfigId: currentProduct.priceConfigId,
                  },
                },
              });
              const maybeDraftId = response.data?.createStationeryDraft.id;

              if (maybeDraftId) {
                cardDraftCreated({
                  draftId: maybeDraftId,
                  themeId: template.themeId,
                  stationeryTemplateCategory: template.category,
                  page: 'pdpModal',
                  existingDraftsCount,
                  subtotalInMinorUnits,
                  isFavorite: !!template.isFavorite,
                  isDigital: false,
                  isPremiumDesign: template.premium,
                  format: 'paper',
                });
                goToCustomizeDraft(maybeDraftId);
              }
            } catch (error) {
              // no-op
            } finally {
              // no-op
            }
          },
          () => {
            // This callback is called when the warning dialog is about to be shown.
            existingDraftsWarningDialog.enter();
          },
        ),
    );
  });

  return {
    handleOnCustomizeCardClick,
    loading,
  };
};
